import { standardInflectors } from '@orbit/serializers'
import { RawTableSettings, TableSettings } from './types'
import { Column } from '../utils'
import { getStorage } from 'lib/utils'
const { dasherize } = standardInflectors

function validateVisibleColumns(visibleColumns, columns, showClientId) {
  if(visibleColumns === null) {
    visibleColumns = ['leadCount', 'goodLeadCount', 'goodLeadRate', 'outlay'].concat(showClientId ? ['clientId'] : ['programGroupId', 'degreeProgramId'])
  }

  // Ensure that visibleColumns entries are all valid columns, and ordered in the same order as they appear in `columns`
  visibleColumns = columns.map(column => column.field).filter(field => visibleColumns.includes(field))


  // At least one groupable column has to be selected and must occur before the ungroupable columns
  if(!columns.some(column => column.groupable && visibleColumns.includes(column.field))) {
    visibleColumns = [columns.find(column => column.groupable).field, ...visibleColumns]
  }

  return visibleColumns
}

function validateSubtotalColumns(subtotalColumns, columns) {
  if(subtotalColumns === null) {
    subtotalColumns = []
  }

  const filteredColumnList = subtotalColumns.filter(field => columns.map(c => c.field).includes(field))
  if(filteredColumnList.length !== subtotalColumns.length) {
    subtotalColumns = filteredColumnList
  }

  return subtotalColumns
}

function validateSortColumn(sortColumn, columns) {
  if(sortColumn === null) {
    sortColumn = 'billableLeadCount'
  }

  if(!columns.map(c => c.field).includes(sortColumn)) {
    sortColumn = null
  }

  return sortColumn
}

function validateRowFilters(rowFilters, columns) {
  if(rowFilters === null) {
    rowFilters = []
  }

  const validRowFilters = rowFilters.filter(rowFilter => columns.map(c => dasherize(c.field)).includes(rowFilter.field))
  if(validRowFilters.length !== rowFilters.length) {
    rowFilters = validRowFilters
  }

  return rowFilters
}

function validateColumnFilters(columnFilters, visibleColumns) {
  if(columnFilters === null) {
    columnFilters = {}
  }

  const validColumnFilters = Object.entries(columnFilters).filter(([columnField, _filter]) => visibleColumns.includes(columnField))
  if(validColumnFilters.length !== Object.entries(columnFilters).length) {
    columnFilters = validColumnFilters
  }

  return columnFilters
}

function validateSavedReportName(savedReportName) {
  if(savedReportName?.length === 0) {
    savedReportName = null
  }

  return savedReportName
}

function validateSavedReportId(savedReportId) {
  if(savedReportId?.length === 0) {
    savedReportId = null
  }

  return savedReportId
}

export default function validateTableSettings(tableSettings: RawTableSettings, columns: Column[], userHasMultipleClients: boolean): TableSettings {
  const visibleColumns = validateVisibleColumns(tableSettings.visibleColumns, columns, userHasMultipleClients)
  if(visibleColumns !== tableSettings.visibleColumns) {
    tableSettings = {...tableSettings, visibleColumns}
  }

  const subtotalColumns = validateSubtotalColumns(tableSettings.subtotalColumns, columns)
  if(subtotalColumns !== tableSettings.subtotalColumns) {
    tableSettings = {...tableSettings, subtotalColumns}
  }

  const sortColumn = validateSortColumn(tableSettings.sortColumn, columns)
  if(sortColumn !== tableSettings.sortColumn) {
    tableSettings = {...tableSettings, sortColumn}
  }

  const sortOrder = tableSettings.sortOrder ?? -1
  if(sortOrder !== tableSettings.sortOrder) {
    tableSettings = {...tableSettings, sortOrder}
  }

  const reportingMode = tableSettings.reportingMode ?? getStorage('PerformanceTable.reportingMode') ?? 'cohort'
  if(reportingMode !== tableSettings.reportingMode) {
    tableSettings = {...tableSettings, reportingMode}
  }

  const rowFilters = validateRowFilters(tableSettings.rowFilters, columns)
  if(rowFilters !== tableSettings.rowFilters) {
    tableSettings = {...tableSettings, rowFilters}
  }

  const columnFilters = validateColumnFilters(tableSettings.columnFilters, tableSettings.visibleColumns)
  if(columnFilters !== tableSettings.columnFilters) {
    tableSettings = {...tableSettings, columnFilters}
  }

  const savedReportName = validateSavedReportName(tableSettings.savedReportName)
  if(savedReportName !== tableSettings.savedReportName) {
    tableSettings = {...tableSettings, savedReportName}
  }

  const savedReportId = validateSavedReportId(tableSettings.savedReportId)
  if(savedReportId !== tableSettings.savedReportId) {
    tableSettings = {...tableSettings, savedReportId}
  }

  return tableSettings as TableSettings
}
